import { DropdownOption } from "PFComponents/dropdown/dropdown";
import NoBackdropDropdownButton from "PFComponents/dropdown/no_backdrop_dropdown_button";
import { Icon } from "PFComponents/icon";
import { CustomValue } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSkillActionPermissions } from "../../hooks/use_skill_action_permissions";
import { SkillsManagementModalType } from "../../parts/modals/import/constants/skills_management_modal_type";
import { useSkillsCustomTypeId } from "../../utils/use_skills_custom_type";
import { SkillCounter } from "../contexts/skills_management_context_provider";
import { useSkillActions } from "../hooks/use_skill_actions";
import { useSkillManagementModal } from "../hooks/use_skill_management_modal";
import { useSkillManagementSidePanel } from "../hooks/use_skill_management_side_panel";
import css from "./skill_actions.module.scss";

type SkillActionsProps = {
  skill: CustomValue;
  selectedCounter: SkillCounter;
};

export const SkillActions = ({ skill, selectedCounter }: SkillActionsProps) => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "table.cells.actions" });
  const { openModal } = useSkillManagementModal();
  const customTypeId = useSkillsCustomTypeId();
  const { approveSkills, blacklistSkills, resetSkills } = useSkillActions();
  const { openSidePanel } = useSkillManagementSidePanel();
  const { isActionDisabledMap } = useSkillActionPermissions(skill);

  const options: DropdownOption[] = useMemo(() => {
    const isBlockedTab = selectedCounter === SkillCounter.Blocked;

    if (isBlockedTab) {
      return [
        { id: "inspect", displayElement: t("inspect"), item: () => openSidePanel(skill.id) },
        {
          id: "approve",
          displayElement: t("approve"),
          item: () => approveSkills({ ids: [skill.id], customTypeId })
        }
      ];
    }

    return [
      { id: "inspect", displayElement: t("inspect"), item: () => openSidePanel(skill.id) },
      {
        id: "edit",
        disabled: isActionDisabledMap.edit,
        displayElement: t("edit"),
        item: () => openModal(SkillsManagementModalType.Edit, { skills: [skill] })
      },
      {
        id: "merge",
        displayElement: t("merge"),
        item: () => openModal(SkillsManagementModalType.Merge, { skills: [skill] }),
        disabled: isActionDisabledMap.merge
      },
      {
        id: "approve",
        displayElement: t("approve"),
        item: () => approveSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.approve
      },
      {
        id: "blacklist",
        displayElement: t("block"),
        item: () => blacklistSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.block
      },
      {
        id: "reset",
        displayElement: t("reset"),
        item: () => resetSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.reset
      }
    ];
  }, [
    selectedCounter,
    t,
    isActionDisabledMap,
    openSidePanel,
    skill,
    approveSkills,
    customTypeId,
    openModal,
    blacklistSkills,
    resetSkills
  ]);

  return (
    <div className={css.container}>
      <NoBackdropDropdownButton buttonKind="blank" options={options} handleChange={(fn) => fn?.()}>
        <Icon name="menu-vertical" size="sm" color="paletteBlue0" />
      </NoBackdropDropdownButton>
    </div>
  );
};
